"use client";

import React from "react";
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {Form, FormControl, FormField, FormItem, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Button} from "@/components/ui/button";
import {getSession, signIn} from "next-auth/react";
import Cookies from "js-cookie";
import {useSearchParams} from "next/navigation";
import {cn} from "@/lib/utils";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Label} from "@/components/ui/label";
import Link from "next/link";
import Image from "next/image";
import {useMutation} from "@tanstack/react-query";

const formSchema = z.object({
    email: z.string().min(1, {message: 'L\'email è obbligatorio.'}),
    password: z.string().min(1, {message: 'La password è obbligatoria.'}),
});

type FormData = z.infer<typeof formSchema>;

interface AccediClientProps {
    isGoogleLoginEnabled: boolean;
}

export default function AccediClient({ isGoogleLoginEnabled }: AccediClientProps) {
    const searchParams = useSearchParams();
    const callbackUrl = searchParams?.get('callbackUrl') || "/account/attivita";
    const redirectUrl = callbackUrl.startsWith("/") && callbackUrl !== "/" ? callbackUrl : "/account/attivita";

    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const loginMutation = useMutation({
        mutationFn: async (data: FormData) => {
            const response = await signIn('credentials', {
                email: data.email,
                password: data.password,
                redirect: false,
            });

            if (!response?.ok) {
                throw new Error(response?.error || 'Errore durante il processo di autenticazione. Riprova.');
            }

            return response;
        },
        onSuccess: async () => {
            const session = await getSession();
            if (session?.user) {
                const user = session.user as any;
                const userData = {
                    tipoUtenteListino: user.tipoUtenteListino || 'PRI',
                    id: user.id,
                    nome: user.nome || user.name || '',
                    cognome: user.cognome || '',
                    email: user.email || '',
                };
                Cookies.set('userData', JSON.stringify(userData), {
                    expires: 1,
                    sameSite: 'strict',
                    secure: window.location.protocol === 'https:',
                });
            }
            window.location.assign(redirectUrl);
        },
    });

    const onSubmit = (data: FormData) => {
        loginMutation.mutate(data);
    };

    const loginError = loginMutation.error?.message ?? '';
    const isEmailNotVerified = loginError.includes('Email non verificata') || loginError.includes('EMAIL_NOT_VERIFIED');

    return (
        <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
            <div className="flex w-full max-w-sm flex-col gap-6">
                <Link href="/" className="flex items-center gap-2 self-center font-medium">
                    <Image
                        src={"/logo-vettoriale.svg"}
                        alt="Gruppo Piccirillo logo"
                        width={70}
                        height={70}
                        priority={true}
                        className="h-10 w-auto"
                    />
                </Link>

                <div className={cn("flex flex-col gap-6")}>
                    <Card>
                        <CardHeader className="text-center">
                            <CardTitle className="text-xl">Bentornato</CardTitle>
                            <CardDescription>
                                {isGoogleLoginEnabled ? "Accedi con Google o usa le tue credenziali" : "Usa le tue credenziali per accedere"}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-6">
                                {isGoogleLoginEnabled && (
                                    <div className="flex flex-col gap-4">
                                        <Button
                                            variant="outline"
                                            className="w-full"
                                            onClick={() => signIn("google", {callbackUrl})}
                                            disabled={loginMutation.isPending}
                                            type="button"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 mr-2">
                                                <path
                                                    d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                            Accedi con Google
                                        </Button>
                                    </div>
                                )}

                                {isGoogleLoginEnabled && (
                                    <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                                        <span className="bg-card text-muted-foreground relative z-10 px-2">
                                            Oppure continua con
                                        </span>
                                    </div>
                                )}

                                <Form {...form}>
                                    <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
                                        <FormField
                                            control={form.control}
                                            name="email"
                                            render={({field, fieldState}) => (
                                                <FormItem>
                                                    <div className="grid gap-3">
                                                        <Label htmlFor="email">Email</Label>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                id="email"
                                                                type="email"
                                                                placeholder="m@example.com"
                                                                required
                                                            />
                                                        </FormControl>
                                                    </div>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="password"
                                            render={({field, fieldState}) => (
                                                <FormItem>
                                                    <div className="grid gap-3">
                                                        <div className="flex items-center">
                                                            <Label htmlFor="password">Password</Label>
                                                            <Link
                                                                href="/auth/recupera-password"
                                                                className="ml-auto text-sm underline-offset-4 hover:underline text-[#001AFF] hover:text-[#000D7D]"
                                                            >
                                                                Password dimenticata?
                                                            </Link>
                                                        </div>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                id="password"
                                                                type="password"
                                                                required
                                                            />
                                                        </FormControl>
                                                    </div>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )}
                                        />

                                        {loginMutation.isError && (
                                            <div className="text-sm text-red-600 text-center p-3 bg-red-50 rounded-md border border-red-200">
                                                {isEmailNotVerified ? (
                                                    <div>
                                                        <p>Email non verificata. Controlla la tua casella di posta.</p>
                                                        <Link
                                                            href={`/auth/verifica-email-inviata?email=${encodeURIComponent(loginMutation.variables?.email ?? '')}`}
                                                            className="underline text-[#001AFF] hover:text-[#000D7D] mt-2 inline-block"
                                                        >
                                                            Invia nuovamente l&apos;email di verifica
                                                        </Link>
                                                    </div>
                                                ) : (
                                                    loginError
                                                )}
                                            </div>
                                        )}

                                        <Button
                                            type="submit"
                                            className="text-white w-full bg-[#000D7D]"
                                            disabled={loginMutation.isPending}
                                        >
                                            {loginMutation.isPending ? "Caricamento..." : "Accedi"}
                                        </Button>
                                    </form>
                                </Form>

                                <div className="text-center text-sm">
                                    Non hai un account?{" "}
                                    <Link
                                        href="/auth/registrati"
                                        className="underline underline-offset-4 text-[#001AFF] hover:text-[#000D7D]"
                                    >
                                        Registrati
                                    </Link>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="text-muted-foreground text-center text-xs text-balance">
                        Cliccando su continua, accetti i nostri{" "}
                        <Link href="#" className="underline underline-offset-4 hover:text-primary">
                            Termini di servizio
                        </Link>{" "}
                        e la nostra{" "}
                        <Link href="#" className="underline underline-offset-4 hover:text-primary">
                            Informativa sulla privacy
                        </Link>.
                    </div>
                </div>
            </div>
        </div>
    );
}
